import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, Loader2, Building, Users, Clock, Sun, CloudSun, CheckCircle 
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { InfoBanner } from '../../components/ui/info-banner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MesaiTakipPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [hasTask, setHasTask] = useState(false);
  const [subcontractors, setSubcontractors] = useState([]);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [recordingId, setRecordingId] = useState(null);

  const getTodayFormatted = () => {
    const today = new Date();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return today.toLocaleDateString('tr-TR', options);
  };

  useEffect(() => {
    checkAttendanceTask();
  }, []);

  const checkAttendanceTask = async () => {
    setLoading(true);
    try {
      // Önce yeni taşeron bazlı sistemi kontrol et
      const newRes = await axios.get(`${API}/my/subcontractor-attendance-tasks`, { headers: getAuthHeaders() });
      
      if (newRes.data.has_task && newRes.data.subcontractors?.length > 0) {
        setHasTask(true);
        setSubcontractors(newRes.data.subcontractors || []);
        setLoading(false);
        return;
      }
      
      // Yeni sistem yoksa eski sisteme bak (geriye uyumluluk)
      const oldRes = await axios.get(`${API}/my/attendance-task`, { headers: getAuthHeaders() });
      setHasTask(oldRes.data.has_task);
      setSubcontractors(oldRes.data.subcontractors || []);
      
      if (!oldRes.data.has_task) {
        toast.error('Aktif mesai takip göreviniz bulunmuyor');
        navigate('/dashboard/my-info');
      }
    } catch (error) {
      console.error('Görev kontrolünde hata:', error);
      toast.error('Görev bilgisi alınamadı');
      navigate('/dashboard/my-info');
    }
    setLoading(false);
  };

  const loadEmployees = async (subId) => {
    setEmployeesLoading(true);
    setSelectedSubcontractor(subId);
    try {
      // Önce yeni endpoint'i dene
      let res;
      try {
        res = await axios.get(`${API}/my/subcontractor-attendance-employees/${subId}`, { 
          headers: getAuthHeaders() 
        });
      } catch (newErr) {
        // Yeni endpoint yoksa eski endpoint'i dene
        res = await axios.get(`${API}/my/attendance-task/employees/${subId}`, { 
          headers: getAuthHeaders() 
        });
      }
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Çalışanlar yüklenirken hata:', error);
      toast.error('Çalışanlar yüklenemedi');
      setEmployees([]);
    }
    setEmployeesLoading(false);
  };

  const recordAttendance = async (employeeId, period) => {
    setRecordingId(`${employeeId}-${period}`);
    try {
      // Önce yeni endpoint'i dene
      let res;
      try {
        res = await axios.post(
          `${API}/my/subcontractor-attendance-record`,
          null,
          { 
            params: { employee_id: employeeId, period },
            headers: getAuthHeaders() 
          }
        );
      } catch (newErr) {
        // Yeni endpoint yoksa eski endpoint'i dene
        res = await axios.post(
          `${API}/my/attendance-task/record`,
          null,
          { 
            params: { employee_id: employeeId, period },
            headers: getAuthHeaders() 
          }
        );
      }
      toast.success(res.data.message);
      // Reload employees to update times
      if (selectedSubcontractor) {
        loadEmployees(selectedSubcontractor);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Kayıt yapılamadı';
      toast.error(msg);
    }
    setRecordingId(null);
  };

  const getSelectedSubcontractorName = () => {
    const sub = subcontractors.find(s => s.id === selectedSubcontractor);
    return sub?.name || 'Taşeron';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="mesai-takip-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!hasTask) {
    return null;
  }

  return (
    <div className="p-6 space-y-6" data-testid="mesai-takip-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/dashboard/my-info')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Taşeron Mesai Takibi</h1>
            <p className="text-gray-500">Taşeron çalışanlarının mesai girişlerini kaydedin</p>
          </div>
        </div>
      </div>

      {/* Today's Date */}
      <div className="text-center py-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
        <p className="text-2xl font-bold text-amber-700">{getTodayFormatted()}</p>
      </div>

      {/* Bilgilendirme Banner */}
      <InfoBanner storageKey="taseron-mesai-takip-info">
        Taşeron mesai takip sisteminde yöneticinizin size taşeron puantajı tutma görevi atar. 
        Bu görevde bildirilen taşeron çalışanlarının görev yerlerinde mesaide olduğunu gördükten sonra 
        tabloya işaretlemeniz gerekmektedir.
      </InfoBanner>

      {/* Subcontractor Selection - Button Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5 text-amber-600" />
            Taşeron Şirket Seçin
          </CardTitle>
        </CardHeader>
        <CardContent>
          {subcontractors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Building className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Görevinize atanmış taşeron şirket bulunamadı</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {subcontractors.map(sub => (
                <Button
                  key={sub.id}
                  variant={selectedSubcontractor === sub.id ? 'default' : 'outline'}
                  className={`h-20 text-lg font-semibold ${
                    selectedSubcontractor === sub.id 
                      ? 'bg-amber-600 hover:bg-amber-700' 
                      : 'hover:bg-amber-50 hover:border-amber-300'
                  }`}
                  onClick={() => loadEmployees(sub.id)}
                  data-testid={`subcontractor-btn-${sub.id}`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <Building className="h-6 w-6" />
                    <span>{sub.name}</span>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee List with Attendance Buttons */}
      {selectedSubcontractor && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-600" />
              {getSelectedSubcontractorName()} - Çalışan Listesi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {employeesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
              </div>
            ) : employees.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Bu taşeron şirkete ait çalışan bulunamadı</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-bold">AD SOYAD</TableHead>
                      <TableHead className="font-bold">GÖREV</TableHead>
                      <TableHead className="font-bold">MESLEK</TableHead>
                      <TableHead className="text-center font-bold">
                        <div className="flex items-center justify-center gap-1">
                          <Sun className="h-4 w-4 text-amber-500" />
                          SABAH (08:00)
                        </div>
                      </TableHead>
                      <TableHead className="text-center font-bold">
                        <div className="flex items-center justify-center gap-1">
                          <CloudSun className="h-4 w-4 text-orange-500" />
                          ÖĞLE (17:00)
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map(emp => (
                      <TableRow key={emp.id} className="hover:bg-gray-50" data-testid={`employee-row-${emp.id}`}>
                        <TableCell className="font-medium">{emp.name || '-'}</TableCell>
                        <TableCell>{emp.position_name || '-'}</TableCell>
                        <TableCell>{emp.profession || '-'}</TableCell>
                        <TableCell className="text-center">
                          {emp.sabah_time ? (
                            <div className="inline-flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg">
                              <CheckCircle className="h-4 w-4" />
                              <span className="font-bold">{emp.sabah_time}</span>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-amber-500 hover:bg-amber-600 text-white"
                              onClick={() => recordAttendance(emp.id, 'sabah')}
                              disabled={recordingId === `${emp.id}-sabah`}
                              data-testid={`sabah-btn-${emp.id}`}
                            >
                              {recordingId === `${emp.id}-sabah` ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <Clock className="h-4 w-4 mr-1" />
                              )}
                              İşe Giriş
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {emp.oglen_time ? (
                            <div className="inline-flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg">
                              <CheckCircle className="h-4 w-4" />
                              <span className="font-bold">{emp.oglen_time}</span>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-orange-500 hover:bg-orange-600 text-white"
                              onClick={() => recordAttendance(emp.id, 'oglen')}
                              disabled={recordingId === `${emp.id}-oglen`}
                              data-testid={`oglen-btn-${emp.id}`}
                            >
                              {recordingId === `${emp.id}-oglen` ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <Clock className="h-4 w-4 mr-1" />
                              )}
                              İşe Giriş
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Info Box */}
            <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <strong>Bilgi:</strong> Her çalışan için sabah (08:00) ve öğle (17:00) mesai girişini kaydedebilirsiniz. 
                Kayıtlar otomatik olarak şube yönetimine bildirim olarak iletilir.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MesaiTakipPage;
